import { useValue } from "@legendapp/state/react";
import type { GraphConfig } from "@repo/shared/types/graph-config";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { Spinner } from "@repo/ui/ui/spinner";
import { useState } from "react";
import { toast } from "sonner";

import { trpc } from "~/libs/trpc.lib";
import { getRouter } from "~/router";
import { boards$ } from "~/stores/boards";

/**
 * The run-result "more options" menu shared by the chat run table and chart:
 * an optional Download CSV item plus a "Send to board" submenu. Owns its own
 * `boards$` subscription so the parent doesn't re-render on board changes
 * (push-subscriptions-down). Sending creates a dataset + block server side via
 * `runs.saveToBoard`; the entities arrive over the org Ably channel. Pass a
 * `graphConfig` to promote the run as a chart (GRAPH) block instead of a table.
 */
export const RunResultActions = ({
  runId,
  name,
  query,
  graphConfig,
  onDownload,
  canDownload = false,
}: {
  runId: string | null;
  name: string;
  query?: string;
  /** When set, "Send to board" creates a GRAPH block with this config. */
  graphConfig?: GraphConfig;
  /** Omit to hide the Download CSV item (e.g. for charts). */
  onDownload?: () => void;
  canDownload?: boolean;
}) => {
  const [sending, setSending] = useState(false);
  const allBoards = useValue(boards$);
  const boardList = Object.values(allBoards ?? {}).filter((b) => b?.id);

  const handleSendToBoard = async (boardId: string) => {
    if (!runId) return;
    setSending(true);
    try {
      const result = await trpc.runs.saveToBoard.mutate({
        runId,
        boardId,
        name: name.trim() || (graphConfig ? "Chart" : "Query result"),
        query,
        ...(graphConfig && { displayType: "GRAPH" as const, graphConfig }),
      });
      toast.success("Saved to board", {
        action: {
          label: "View board",
          onClick: () =>
            getRouter().navigate({ to: `/boards/${result.boardId}` }),
        },
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send to board.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button size="icon-sm" variant="ghost" disabled={sending}>
            {sending ? (
              <Spinner className="size-3.5" />
            ) : (
              <Icon name="ellipsis-vertical" className="size-3.5" />
            )}
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-auto min-w-32 max-w-96">
        {onDownload && (
          <DropdownMenuItem disabled={!canDownload} onClick={onDownload}>
            <Icon name="document-arrow-down" className="size-4" />
            Download CSV
          </DropdownMenuItem>
        )}
        {runId && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon name="dashboard" className="size-4" />
              Send to board...
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent sideOffset={8} alignOffset={-4}>
              {boardList.map((board) => (
                <DropdownMenuItem
                  key={board.id}
                  onClick={() => void handleSendToBoard(board.id)}
                >
                  <AvatarDisplay
                    avatar={board.avatar}
                    fallbackIcon="dashboard"
                  />
                  {board.name || "Untitled board"}
                </DropdownMenuItem>
              ))}
              {boardList.length === 0 && (
                <DropdownMenuItem disabled>No boards</DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
